import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, PackageSearch, AlertTriangle, RotateCw, Clock, ChevronRight } from "lucide-react";
import { BrandHeader } from "@/components/rastreo/BrandHeader";
import { TipoIcon } from "@/components/rastreo/TipoIcon";
import { buscarUnidad, clasificarError, mensajeError, api } from "@/lib/api";
import { nombreTipo, iconoTipo } from "@/lib/format";
import { RASTREO } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";

export default function Home() {
  usePageTitle("Rastreá tu unidad");
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null); // { coincidencias } | null
  const [buscado, setBuscado] = useState(false);
  const [tipoError, setTipoError] = useState(null); // null | "servicio" | "limite" | "invalido"
  const [msgLimite, setMsgLimite] = useState("");
  const [tipos, setTipos] = useState([]);

  useEffect(() => {
    api.get("/tipos").then((r) => setTipos(r.data)).catch(() => {});
  }, []);

  const buscar = async (termino) => {
    setCargando(true);
    setBuscado(false);
    setTipoError(null);
    try {
      const data = await buscarUnidad(termino);
      const c = data.coincidencias || [];
      if (c.length === 1) {
        navigate(`/unidad/${c[0].vin}`);
        return;
      }
      setResultado(data);
      setBuscado(true);
    } catch (err) {
      // Un fallo del servicio o el límite de consultas no son lo mismo que
      // "esa unidad no existe": cada caso muestra su propio mensaje.
      const tipo = clasificarError(err);
      if (tipo === "no-encontrado") {
        setResultado({ coincidencias: [] });
      } else {
        setTipoError(tipo);
        if (tipo === "limite") setMsgLimite(mensajeError(err, "Estás realizando muchas consultas. Esperá unos minutos e intentá de nuevo."));
      }
      setBuscado(true);
    } finally {
      setCargando(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const limpio = q.trim();
    if (limpio) buscar(limpio);
  };

  const coincidencias = resultado?.coincidencias || [];

  return (
    <div className="min-h-screen bg-[var(--tc-surface)]">
      <BrandHeader />

      <main className="mx-auto max-w-2xl px-5">
        <section className="pt-16 pb-8 sm:pt-24 tc-fade-up">
          <h1 className="text-center text-3xl font-semibold tracking-tight text-[var(--tc-text)] sm:text-4xl">
            Rastreá tu unidad
          </h1>
          <p className="mx-auto mt-3 max-w-md text-center text-[15px] leading-relaxed text-[var(--tc-text-secondary)]">
            Ingresá los últimos 6 dígitos del número de serie (VIN) de tu unidad,
            o el número de pedido o embarque, y mirá en qué punto del viaje está.
          </p>

          <form onSubmit={onSubmit} className="mt-8">
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Search
                  size={20}
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--tc-text-secondary)]"
                />
                <input
                  data-testid={RASTREO.searchInput}
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ej.: 413620"
                  inputMode="text"
                  autoCapitalize="characters"
                  className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white py-4 pl-12 pr-4 text-lg tracking-wide text-[var(--tc-text)] outline-none transition-colors placeholder:text-[var(--tc-state-pending)] focus:border-[var(--tc-accent)] focus:ring-2 focus:ring-[var(--tc-accent-soft)]"
                />
              </div>
              <button
                data-testid={RASTREO.searchButton}
                type="submit"
                disabled={cargando || !q.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-7 py-4 text-base font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {cargando ? <Loader2 size={20} className="animate-spin" /> : <Search size={19} />}
                Rastrear
              </button>
            </div>
          </form>

          <p
            data-testid={RASTREO.helperStrip}
            className="mt-5 text-center text-[13px] text-[var(--tc-text-secondary)]"
          >
            Torre de Control es la plataforma de trazabilidad de LOCCITEC para
            seguir la importación de camiones y maquinaria hasta su entrega.
          </p>
        </section>

        {/* Resultados */}
        {buscado && tipoError === "limite" && <LimiteExcedido mensaje={msgLimite} />}

        {buscado && tipoError === "servicio" && <ErrorBuscador onReintentar={() => buscar(q.trim())} />}

        {buscado && tipoError === "invalido" && (
          <ErrorBuscador
            onReintentar={() => buscar(q.trim())}
            titulo="No pudimos procesar esa búsqueda"
            descripcion="Revisá lo que escribiste (muy largo o con caracteres raros) e intentá de nuevo."
          />
        )}

        {buscado && !tipoError && coincidencias.length === 0 && <NoEncontrado />}

        {buscado && !tipoError && coincidencias.length > 1 && (
          <Desambiguacion coincidencias={coincidencias} tipos={tipos} onIr={(vin) => navigate(`/unidad/${vin}`)} />
        )}
      </main>

      <footer className="mx-auto max-w-2xl px-5 py-12 text-center text-xs text-[var(--tc-text-secondary)]">
        LOCCITEC · Operador logístico · Importación de vehículos y maquinaria pesada
      </footer>
    </div>
  );
}

function NoEncontrado() {
  return (
    <div
      data-testid={RASTREO.notFound}
      className="mt-4 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-8 text-center tc-fade-up"
    >
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
        <PackageSearch size={26} />
      </span>
      <h2 className="mt-4 text-lg font-semibold text-[var(--tc-text)]">
        No encontramos esa unidad
      </h2>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[var(--tc-text-secondary)]">
        Revisá que los últimos 6 dígitos del VIN estén bien escritos. También
        podés ingresar el número de serie completo, o el número de pedido o embarque.
      </p>
    </div>
  );
}

function ErrorBuscador({ onReintentar, titulo = "No pudimos buscar tu unidad", descripcion = "Hubo un problema con el servicio, no es que tu unidad no exista. Intentá de nuevo en un momento." }) {
  return (
    <div
      data-testid={RASTREO.searchError}
      className="mt-4 rounded-[var(--tc-radius)] border border-red-200 bg-red-50 p-8 text-center tc-fade-up"
    >
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-red-500">
        <AlertTriangle size={26} />
      </span>
      <h2 className="mt-4 text-lg font-semibold text-[var(--tc-text)]">{titulo}</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[var(--tc-text-secondary)]">
        {descripcion}
      </p>
      <button
        data-testid={RASTREO.searchErrorRetry}
        onClick={onReintentar}
        className="mt-5 inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
      >
        <RotateCw size={16} /> Reintentar
      </button>
    </div>
  );
}

// El límite de consultas (429) no ofrece "reintentar" inmediato a propósito:
// reintentar ahora mismo solo devolvería el mismo 429.
function LimiteExcedido({ mensaje }) {
  return (
    <div
      data-testid={RASTREO.rateLimitError}
      className="mt-4 rounded-[var(--tc-radius)] border border-amber-200 bg-amber-50 p-8 text-center tc-fade-up"
    >
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-amber-600">
        <Clock size={26} />
      </span>
      <h2 className="mt-4 text-lg font-semibold text-[var(--tc-text)]">Estás consultando muy rápido</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[var(--tc-text-secondary)]">
        {mensaje}
      </p>
    </div>
  );
}

function Desambiguacion({ coincidencias, tipos, onIr }) {
  return (
    <div className="mt-4 tc-fade-up">
      <p className="mb-3 text-sm text-[var(--tc-text-secondary)]">
        Encontramos <strong className="text-[var(--tc-text)]">{coincidencias.length}</strong>{" "}
        unidades con ese número. Elegí la tuya:
      </p>
      <ul data-testid={RASTREO.disambiguationList} className="flex flex-col gap-3">
        {coincidencias.map((u) => (
          <li key={u.id}>
            <button
              data-testid={RASTREO.disambiguationCard}
              onClick={() => onIr(u.vin)}
              className="group flex w-full items-center gap-4 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-4 text-left transition-colors hover:border-[var(--tc-accent)]"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
                <TipoIcon icono={iconoTipo(tipos, u.tipo)} size={20} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[var(--tc-text)]">
                  {u.marca} {u.modelo}
                </p>
                <p className="truncate font-mono text-xs text-[var(--tc-text-secondary)]">
                  {u.vin}
                </p>
                <p className="mt-1 text-xs text-[var(--tc-text-secondary)]">
                  {[nombreTipo(tipos, u.tipo), u.color].filter(Boolean).join(" · ")}
                  {u.etapa_actual && (
                    <> · <span className="font-medium text-[var(--tc-accent)]">{u.etapa_actual.nombre}</span></>
                  )}
                </p>
              </div>
              <ChevronRight
                size={20}
                className="shrink-0 text-[var(--tc-state-pending)] transition-colors group-hover:text-[var(--tc-accent)]"
              />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
